import {Rule, Source} from 'Event/attendee-rules'
import {NESTED_RULE} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/NestedRule'
import RuleList from 'Event/attendee-rules/RuleConfig/RuleList'
import RulesClipboardActions from 'Event/attendee-rules/RulesClipboardActions'
import CheckedInFilter from 'organization/Event/AttendeeManagement/AttendeeFilterDialog/CheckedInFilter'
import {useBroadcastForm} from 'organization/Event/Broadcasts/BroadcastForm'
import {FilterRules} from 'organization/Event/Broadcasts/BroadcastsProvider'
import {useTemplate} from 'Event/TemplateProvider'
import React from 'react'
import {Controller} from 'react-hook-form'
import styled from 'styled-components'
import Button from 'lib/ui/Button'

const BROADCAST_SOURCES: Source[] = ['Tags', 'Group', 'Nested Rule']

export default function AttendeeFilterInput(props: {
  onDone: () => void
  showing: boolean
}) {
  const {onDone, showing} = props
  const {broadcast, control} = useBroadcastForm()
  const template = useTemplate()

  const defaultValue: FilterRules | null =
    broadcast?.attendee_segment === 'filtered' && broadcast.filter_rules
      ? broadcast.filter_rules
      : {rules: []}

  return (
    <Box showing={showing}>
      <Controller
        name="filter_rules"
        control={control}
        defaultValue={defaultValue}
        render={({value, onChange}) => {
          const filterRules: FilterRules = value || {rules: []}

          const updateField = (updates: Partial<FilterRules>) => {
            onChange({...filterRules, ...updates})
          }

          return (
            <>
              <RulesClipboardActions
                rules={filterRules.rules}
                onChange={(rules: Rule[]) =>
                  updateField({rules: keepBroadcastRules(rules)})
                }
              />
              <RuleList
                rules={filterRules.rules}
                onChange={(rules: Rule[]) => updateField({rules})}
                description="Send to attendees matching"
                descriptionHidden={filterRules.rules.length === 0}
                sources={BROADCAST_SOURCES}
              />
              <ClearButton
                variant="outlined"
                fullWidth
                color="danger"
                onClick={() => {
                  onChange({rules: []})
                  onDone()
                }}
              >
                Clear Filter
              </ClearButton>
              <StatusFilters>
                <CheckedInFilter
                  isEnabled
                  status={filterRules.is_active ?? null}
                  onChange={(val) => updateField({is_active: val})}
                  label="Enabled status"
                  aria-label="enabled"
                  statusLabel="Enabled"
                />
                <CheckedInFilter
                  isEnabled={template.login.requiresPassword}
                  status={filterRules.password_created ?? null}
                  onChange={(val) => updateField({password_created: val})}
                  label="Password"
                  aria-label="password"
                  statusLabel="Created"
                />
                <CheckedInFilter
                  isEnabled={template.waiver.isEnabled}
                  status={filterRules.waiver_signed ?? null}
                  onChange={(val) => updateField({waiver_signed: val})}
                  label="Waiver"
                  aria-label="waiver"
                  statusLabel="Signed"
                />
                <CheckedInFilter
                  isEnabled={template.techCheck.isEnabled}
                  status={filterRules.tech_check_completed ?? null}
                  onChange={(val) => updateField({tech_check_completed: val})}
                  label="Tech Check"
                  aria-label="tech check"
                  statusLabel="Completed"
                />
                <CheckedInFilter
                  isEnabled
                  status={
                    filterRules.checked_in_status === 'checked_in'
                      ? true
                      : filterRules.checked_in_status === 'not_checked_in'
                      ? false
                      : null
                  }
                  onChange={(val) =>
                    updateField({
                      checked_in_status:
                        val === true
                          ? 'checked_in'
                          : val === false
                          ? 'not_checked_in'
                          : null,
                    })
                  }
                  label="Check in status"
                  aria-label="check in"
                  statusLabel="Checked In"
                />
              </StatusFilters>
            </>
          )
        }}
      />
    </Box>
  )
}

// Pasted rules can come from surfaces that allow more rule sources than
// Broadcast does, so anything outside BROADCAST_SOURCES is dropped —
// including rules nested inside a Nested Rule.
function keepBroadcastRules(rules: Rule[]): Rule[] {
  return rules
    .filter((rule) => BROADCAST_SOURCES.includes(rule.source))
    .map((rule) =>
      rule.source === NESTED_RULE
        ? {...rule, rules: keepBroadcastRules(rule.rules)}
        : rule,
    )
}

const Box = styled.div<{showing: boolean}>`
  display: ${(props) => (props.showing ? 'block' : 'none')};
  margin-bottom: ${(props) => props.theme.spacing[4]};
`

const StatusFilters = styled.div`
  margin-top: ${(props) => props.theme.spacing[4]};
`

const ClearButton = styled(Button)`
  margin-bottom: ${(props) => props.theme.spacing[4]};
`
